import React from 'react'
import dayjs from 'dayjs'
import { AiFillEye } from 'react-icons/ai'

export const PAGE_SIZE = 10
const CLOUDFRONT = process.env.REACT_APP_AWS_MEDIA_URL;

/* ------------------------------ helpers ------------------------------ */

export const statusKey = (item) =>
  (item?.status === 'completed' || item?.status === 'in_progress' || item?.status === 'expired' ? item.status : 'assigned')

export const assignmentStatusLabel = (status) =>
  (status === 'in_progress' ? 'In progress' : status === 'completed' ? 'Completed' : status === 'expired' ? 'Expired' : 'Assigned')

export const assignmentStatusClass = (status) => {
  if (status === 'completed') return 'bg-green-50 text-green-600 border-green-100'
  if (status === 'in_progress') return 'bg-blue-50 text-blue-600 border-blue-100'
  if (status === 'expired') return 'bg-red-50 text-red-600 border-red-100'
  return 'bg-slate-50 text-slate-600 border-slate-200'
}

export const verificationClass = (status) => {
  if (status === 'verified') return 'bg-green-50 text-green-600 border-green-100'
  if (status === 'rejected') return 'bg-red-50 text-red-500 border-red-100'
  return 'bg-amber-50 text-amber-600 border-amber-100'
}

export const expiryClass = (label) => {
  if (label === 'Expired') return 'bg-red-50 text-red-500 border-red-100'
  if (label === 'Expiring') return 'bg-amber-50 text-amber-600 border-amber-100'
  if (label === 'Valid') return 'bg-green-50 text-green-600 border-green-100'
  return 'bg-slate-50 text-slate-500 border-slate-200'
}

export function worstExpiry(items) {
  const labels = items.map((item) => item.expiryTracking || 'No expiry')
  if (labels.includes('Expired')) return 'Expired'
  if (labels.includes('Expiring')) return 'Expiring'
  if (labels.includes('Valid')) return 'Valid'
  return 'No expiry'
}

export const isOverdue = (item) =>
  statusKey(item) !== 'completed' && !!item?.dueDate && dayjs(item.dueDate).isBefore(dayjs(), 'day')

export const formatDate = (value) => (value ? dayjs(value).format('MM-DD-YYYY') : '—')

export function materialSummary(row) {
  const materials = Array.isArray(row?.materials) ? row.materials : []
  const total = materials.length || (row?.materialType ? 1 : 0)
  const done = materials.length
    ? materials.filter((material) => material.completed).length
    : (row?.status === 'completed' && row?.materialType ? 1 : 0)
  const videos = materials.filter((material) => material.kind === 'video')
  let percent = null
  if (videos.length) {
    percent = Math.round(videos.reduce((sum, material) => {
      if (material.completed) return sum + 100
      if (material.videoDuration) {
        return sum + Math.min(100, Math.round((Number(material.watchedSeconds) / material.videoDuration) * 100))
      }
      return sum + (Number(material.progressPercent) || 0)
    }, 0) / videos.length)
  } else if (row?.materialType === 'video') {
    if (row.status === 'completed') percent = 100
    else if (row.videoDuration) percent = Math.min(100, Math.round((Number(row.watchedSeconds) / row.videoDuration) * 100))
    else percent = Number(row.progressPercent) || 0
  }
  return { total, done, percent }
}

export function openCredentialFile(item) {
  const raw = String(item.fileName || item.fileUrl || '')
  const url = !raw
    ? ''
    : raw.includes('amazonaws.com/')
      ? `${CLOUDFRONT}${(raw.split('amazonaws.com/')[1] || '').replace(/^\/+/, '')}`
      : raw.startsWith('http')
        ? raw
        : `${CLOUDFRONT}${raw.replace(/^\/+/, '')}`
  if (url) window.open(url, '_blank')
}

/* ------------------------------ grouping ------------------------------ */

function groupBy(items, getKey, getName, getCode) {
  const grouped = new Map()
  items.forEach((item) => {
    const key = getKey(item)
    const group = grouped.get(key) || { key, name: getName(item), code: getCode(item), items: [] }
    group.items.push(item)
    grouped.set(key, group)
  })
  return Array.from(grouped.values())
}

export const groupDocuments = (docs) => groupBy(
  docs,
  (d) => d.employeeCode || d.employeeName || 'unknown',
  (d) => d.employeeName || '-',
  (d) => d.employeeCode || ''
)

export const groupTraining = (assignments) => groupBy(
  assignments,
  (i) => String(i.employeeID || i.employeeCode || i.employeeName || i._id),
  (i) => i.employeeName || 'Employee',
  (i) => i.employeeCode || ''
).map((group) => {
  const counts = { assigned: 0, in_progress: 0, completed: 0 }
  let overdue = 0
  group.items.forEach((item) => {
    counts[statusKey(item)] += 1
    if (isOverdue(item)) overdue += 1
  })
  return { ...group, counts, overdue, completion: Math.round((counts.completed / group.items.length) * 100) }
})

export const groupCertifications = (records) => groupBy(
  records.filter((i) => i.type === 'certification'),
  (i) => i.employeeCode || i.employeeName || i.userId || 'unknown',
  (i) => i.employeeName || 'Employee',
  (i) => i.employeeCode || ''
).map((group) => {
  const counts = { pending: 0, verified: 0, rejected: 0 }
  group.items.forEach((item) => {
    counts[item.verification === 'verified' || item.verification === 'rejected' ? item.verification : 'pending'] += 1
  })
  return { ...group, counts, expiry: worstExpiry(group.items) }
})

/* ------------------------------ UI pieces ------------------------------ */

export function Pill({ className = '', children }) {
  return (
    <span className={`inline-block px-2.5 py-0.5 text-xs font-medium rounded-full border whitespace-nowrap ${className}`}>
      {children}
    </span>
  )
}

const TONES = {
  slate: 'bg-slate-50 border-slate-200 text-slate-700',
  blue: 'bg-blue-50 border-blue-100 text-blue-600',
  green: 'bg-green-50 border-green-100 text-green-600',
  red: 'bg-red-50 border-red-100 text-red-500',
  amber: 'bg-amber-50 border-amber-100 text-amber-600'
}

export function Stat({ label, value, sub, tone = 'slate' }) {
  return (
    <div className={`rounded-lg border px-4 py-4 ${TONES[tone]}`}>
      <p className='text-xs uppercase tracking-wide opacity-80'>{label}</p>
      <p className='mt-1 text-2xl font-bold'>{value}</p>
      {sub ? <p className='mt-0.5 text-xs opacity-80'>{sub}</p> : null}
    </div>
  )
}

export function ViewButton({ onClick, title = 'View details' }) {
  return (
    <button
      type='button'
      title={title}
      aria-label={title}
      onClick={onClick}
      className='w-8 h-8 inline-flex items-center justify-center rounded border border-blue-100 bg-blue-50 text-[#2f5bff] hover:bg-blue-100'
    >
      <AiFillEye className='w-5 h-5' />
    </button>
  )
}

export function DetailModal({ title, subtitle, onClose, children }) {
  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4'>
      <div className='bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col'>
        <div className='flex items-start justify-between p-6 border-b shrink-0'>
          <div>
            <h3 className='text-lg font-semibold text-slate-900'>{title}</h3>
            {subtitle ? <p className='text-sm text-gray-500 mt-1'>{subtitle}</p> : null}
          </div>
          <button type='button' onClick={onClose} className='text-gray-400 hover:text-gray-600' aria-label='Close'>
            <svg className='w-6 h-6' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth='2' d='M6 18L18 6M6 6l12 12' />
            </svg>
          </button>
        </div>
        <div className='flex-1 overflow-y-auto p-6 space-y-5'>{children}</div>
        <div className='flex justify-end p-4 border-t shrink-0'>
          <button type='button' className='text-slate-700 bg-slate-100 hover:bg-slate-200 px-10 py-2.5 text-sm rounded' onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

export function Pagination({ page, pageCount, totalItems, onChange }) {
  if (!totalItems) return null
  const start = Math.max(1, Math.min(page - 2, pageCount - 4))
  const pages = []
  for (let i = start; i <= Math.min(pageCount, start + 4); i += 1) pages.push(i)
  const btn = 'px-3 py-1.5 text-sm border border-slate-200 disabled:opacity-50 disabled:cursor-not-allowed'
  return (
    <div className='flex flex-wrap items-center justify-end gap-4 px-1 pt-4'>
      <span className='text-sm text-gray-500'>Total {totalItems} {totalItems === 1 ? 'Record' : 'Records'}</span>
      <div className='inline-flex'>
        <button type='button' className={`${btn} rounded-l-lg`} disabled={page <= 1} onClick={() => onChange(page - 1)}>Previous</button>
        {pages.map((number) => (
          <button
            key={number}
            type='button'
            onClick={() => onChange(number)}
            className={`${btn} -ml-px ${number === page ? 'bg-blue-50 text-blue-600 border-blue-200' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
          >
            {number}
          </button>
        ))}
        <button type='button' className={`${btn} -ml-px rounded-r-lg`} disabled={page >= pageCount} onClick={() => onChange(page + 1)}>Next</button>
      </div>
    </div>
  )
}

export function DataTable({ headers, rows, empty }) {
  return (
    <div className='overflow-x-auto rounded-lg border border-slate-200'>
      <table className='w-full text-xs text-left text-slate-600'>
        <thead className='bg-slate-50 text-xs text-slate-700 border-b border-slate-200'>
          <tr>
            {headers.map((header) => (
              <th key={header.label} scope='col' className={`py-3 px-4 whitespace-nowrap ${header.center ? 'text-center' : ''}`}>
                {header.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length ? rows : (
            <tr><td className='py-6 px-4 text-center text-gray-500' colSpan={headers.length}>{empty}</td></tr>
          )}
        </tbody>
      </table>
    </div>
  )
}